import { Point, util } from 'fabric';
import React from 'react';

import { Coords, DesignDimensions } from 'editor/src/store/design/types';
import getSettingsValue from 'editor/src/store/editor/selector/getSettingsValue';
import { SettingsProperty } from 'editor/src/store/editor/types';
import { useSelector } from 'editor/src/store/hooks';

import { convertMmToUnit } from 'editor/src/util/math';
import useFabricCanvas from 'editor/src/util/useFabricCanvas';
import useFabricUtils from 'editor/src/util/useFabricUtils';

import RulerGuides from 'editor/src/component/EditorArea/RulerGuides';
import { documentGuideAxis } from 'editor/src/component/EditorArea/RulerGuides/geometry';
import useGuideDrag from 'editor/src/component/EditorArea/RulerGuides/useGuideDrag';
import { CanvasRotation } from 'editor/src/component/EditorArea/types';
import useForceUpdateOnViewportChange from 'editor/src/component/EditorArea/useForceUpdateOnViewportChange';
import getDelimiters from 'editor/src/component/EditorArea/utils/getDelimiters';

import RulersCss from './RulersCss';

interface Props {
  spreadHeight: number;
  spreadWidth: number;
  spreadCoords: Coords;
  dimensions: Pick<DesignDimensions, 'width' | 'height'> & Partial<Pick<DesignDimensions, 'min_width'>>;
  guidesEnabled: boolean;
  rulersVisible: boolean;
  canvasRotation: CanvasRotation;
}

const MIN_LABEL_SPACING = 50; // CSS pixels, including space between 40px labels

function Rulers({
  spreadCoords,
  spreadWidth,
  spreadHeight,
  dimensions,
  guidesEnabled,
  rulersVisible,
  canvasRotation,
}: Props) {
  const fabricCanvas = useFabricCanvas();
  const { mm2px } = useFabricUtils();
  const { draft, startDrag, onPointerMove, onPointerUp, onPointerCancel } = useGuideDrag(
    spreadCoords,
    spreadWidth,
    spreadHeight,
    canvasRotation,
  );

  const unit = useSelector((state) => getSettingsValue(state, SettingsProperty.units));
  const repeatDesign = useSelector((state) => state.hostSettings.repeatDesign);
  useForceUpdateOnViewportChange();

  const bleedWith = Math.abs(spreadWidth - dimensions.width);
  const zoom = fabricCanvas.getZoom();
  const mmPerUnit = 1 / convertMmToUnit(1, unit);
  const targetStep = MIN_LABEL_SPACING / Math.max(mm2px(mmPerUnit) * zoom, 0.000001);
  const magnitude = 10 ** Math.floor(Math.log10(targetStep));
  const multiplier = [1, 2, 5, 10].find((value) => value * magnitude >= targetStep) || 10;
  const rulerStep = Math.max(0.1, multiplier * magnitude) * mmPerUnit;
  // Repeat placement uses the product's width increment, independently of label density.
  const delimiterWidth = dimensions.min_width
    ? dimensions.min_width + bleedWith
    : Math.max(1, 10 ** Math.floor(Math.log10(Math.max(spreadWidth, spreadHeight, 1))) / 2);
  let maxWidth = spreadWidth;
  let offsetX = 0;
  if (repeatDesign) {
    maxWidth = spreadWidth * (repeatDesign.count * 2) + 1;
    offsetX = -delimiterWidth * repeatDesign.count;
  }
  const xStep = repeatDesign ? Math.ceil(rulerStep / delimiterWidth) * delimiterWidth : rulerStep;
  const xDelimiters = getDelimiters(xStep, maxWidth);
  const yDelimiters = getDelimiters(rulerStep, spreadHeight);
  const rotatedAxes = documentGuideAxis('horizontal', canvasRotation.angleDeg) === 'vertical';
  const corners = [
    new Point(spreadCoords.left, spreadCoords.top),
    new Point(spreadCoords.left + mm2px(spreadWidth), spreadCoords.top),
    new Point(spreadCoords.left, spreadCoords.top + mm2px(spreadHeight)),
    new Point(spreadCoords.left + mm2px(spreadWidth), spreadCoords.top + mm2px(spreadHeight)),
  ].map((point) => util.rotatePoint(point, canvasRotation.canvasCenter, canvasRotation.angleRad));
  const rulerCoords = { left: Math.min(...corners.map((p) => p.x)), top: Math.min(...corners.map((p) => p.y)) };
  const reverseX = rotatedAxes ? Math.sin(canvasRotation.angleRad) > 0 : Math.cos(canvasRotation.angleRad) < 0;
  const reverseY = rotatedAxes ? Math.sin(canvasRotation.angleRad) < 0 : Math.cos(canvasRotation.angleRad) < 0;

  return (
    <div
      style={{ position: 'absolute', inset: 0, pointerEvents: 'none', touchAction: 'none' }}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      {/* The ruler bars sit on top of the canvas, so draw them only when the user asked for rulers:
          otherwise they cover the design and swallow pointer events meant for the canvas. */}
      {rulersVisible && (
        <RulersCss
          onGuideStart={
            guidesEnabled
              ? (event, axis) => startDrag(event, documentGuideAxis(axis, canvasRotation.angleDeg))
              : undefined
          }
          spreadHeight={rotatedAxes ? spreadWidth : spreadHeight}
          spreadWidth={rotatedAxes ? spreadHeight : spreadWidth}
          spreadCoords={rulerCoords}
          xDelimiters={rotatedAxes ? yDelimiters : xDelimiters}
          yDelimiters={rotatedAxes ? xDelimiters : yDelimiters}
          unit={unit}
          zoom={zoom}
          dimensions={rotatedAxes ? { width: dimensions.height, height: dimensions.width } : dimensions}
          offsetX={rotatedAxes ? 0 : offsetX}
          reverseX={reverseX}
          reverseY={reverseY}
        />
      )}
      {guidesEnabled && (
        <RulerGuides
          coords={spreadCoords}
          width={spreadWidth}
          height={spreadHeight}
          rotation={canvasRotation}
          draft={draft}
          onPointerDown={startDrag}
        />
      )}
    </div>
  );
}

export default React.memo(Rulers);
